import React, { useEffect, useState, useCallback } from 'react';
import { agents as agentsApi } from '../utils/api';
import { useToast, ToastContainer } from '../hooks/useToast';
import { getUser } from '../utils/auth';
import { useT } from '../i18n/I18nContext';

const EMPTY = { name: '', email: '', whatsapp: '', wechat: '', password: '' };

export default function Agents() {
  const t = useT();
  const [list, setList]       = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const { toasts, toast }       = useToast();
  const currentUser             = getUser();
  const isAdmin                 = currentUser?.role === 'admin';

  const load = useCallback(() => agentsApi.list().then(setList), []);
  useEffect(() => { load(); }, [load]);

  function openNew()   { setForm(EMPTY); setEditing(null); setShowForm(true); }
  function openEdit(a) { setForm({ name: a.name, email: a.email||'', whatsapp: a.whatsapp||'', wechat: a.wechat||'', password: '' }); setEditing(a); setShowForm(true); }
  function sf(k, v)    { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.name) return toast(t('agents.name_required'), 'error');
    setSaving(true);
    try {
      if (editing) { await agentsApi.update(editing.id, form); toast(t('agents.saved'), 'success'); }
      else         { await agentsApi.create(form); toast(t('agents.created'), 'success'); }
      setShowForm(false); load();
    } catch(e) { toast(e.response?.data?.error || t('common.error'), 'error'); }
    finally { setSaving(false); }
  }

  async function handleDeactivate(a) {
    if (!window.confirm(t('agents.deactivate.confirm').replace('{name}', a.name))) return;
    try { await agentsApi.delete(a.id); toast(t('agents.deactivated'), 'default'); load(); }
    catch(e) { toast(e.response?.data?.error || t('common.error'), 'error'); }
  }

  const subtitle = list.length === 1
    ? `1 ${t('agents.title').toLowerCase().replace('s','')} ativo`
    : `${list.length} ${t('agents.title').toLowerCase()} ativos`;

  return (
    <div className="page">
      <ToastContainer toasts={toasts} />
      <div className="page-header">
        <div>
          <div className="page-title">🧑‍💼 {t('agents.title')}</div>
          <div className="page-subtitle">{subtitle}</div>
        </div>
        {isAdmin && <button className="btn btn-primary" onClick={openNew}>{t('agents.new')}</button>}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="table">
          <thead>
            <tr>
              <th>{t('col.name')}</th>
              <th>{t('col.email')}</th>
              <th>WhatsApp</th>
              <th>WeChat</th>
              {isAdmin && <th style={{ width: 100 }}>{t('col.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={isAdmin ? 5 : 4} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 32 }}>{t('agents.empty')}</td></tr>
            )}
            {list.map(a => (
              <tr key={a.id}>
                <td style={{ fontWeight: 600 }}>{a.name}</td>
                <td>{a.email || '—'}</td>
                <td>{a.whatsapp || '—'}</td>
                <td>{a.wechat || '—'}</td>
                {isAdmin && (
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(a)}>✏️</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => handleDeactivate(a)}>🗑️</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">{editing ? t('agents.form.title.edit') : t('agents.form.title.new')}</div>
              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label>{t('agents.form.name')}</label>
                  <input value={form.name} onChange={e => sf('name', e.target.value)} placeholder={t('agents.form.name').replace(' *', '')} autoFocus />
                </div>
                <div className="form-group">
                  <label>{t('col.email')}</label>
                  <input type="email" value={form.email} onChange={e => sf('email', e.target.value)} placeholder="agente@email.com" />
                </div>
                <div className="form-group">
                  <label>WhatsApp</label>
                  <input value={form.whatsapp} onChange={e => sf('whatsapp', e.target.value)} placeholder="+86 ..." />
                </div>
                <div className="form-group">
                  <label>WeChat ID</label>
                  <input value={form.wechat} onChange={e => sf('wechat', e.target.value)} placeholder="ID WeChat" />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label>{editing ? t('agents.form.password_edit') : t('agents.form.password_new')}</label>
                  <input type="password" value={form.password} onChange={e => sf('password', e.target.value)} placeholder={editing ? '••••••••' : '••••••••'} autoComplete="new-password" />
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>{t('agents.form.password_hint')}</div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>{t('btn.cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('common.saving') : t('btn.save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
